import React, { useRef, useState } from 'react';
import { ResultadoDisparo } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
// Overlay compartilhado dos modais do sistema + receita visual do modal irmão
// (reuso por import, nunca cópia — o que é exclusivo daqui vive no CSS ao lado).
import '../../../components/modal/add-car/AddCarModal.css';
import './FornecedorDetalhesModal.css';
import './ResultadoDisparoModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Resultado de um disparo de convite (12/08/2026) — a resposta à crítica de UX do Victor:
 * "Reenviado o quê? Para onde? Nunca sei o que está válido."
 *
 * O backend sempre devolveu o desfecho por canal (destinatário, sucesso, erro) e a tela
 * jogava fora, mostrando um toast fixo — que virava FALSO SUCESSO quando o e-mail falhava
 * (ex.: SMTP não configurado, o caso do ambiente local). Este modal mostra os fatos:
 *
 * - **por canal**: e-mail enviado para quem, ou por que não saiu; WhatsApp é manual e o
 *   modal diz isso em vez de fingir que enviou;
 * - **o link em claro, com "Copiar"**, do jeito que o fornecedor o recebeu;
 * - **se aquele endereço é novo ou é o mesmo** (`linkMudou`) — sem isso, o comprador não
 *   sabe o que continua valendo, que foi a metade mais incômoda da crítica.
 *
 * Serve às quatro ações da linha do fornecedor: Enviar convites (em lote, sem link),
 * Reenviar, Novo link e **Ver link** (12/08/2026 — só link, sem disparo nenhum).
 */

export interface LinhaDisparo {
  fornecedorNome: string;
  resultados: ResultadoDisparo[];
}

interface ResultadoDisparoModalProps {
  titulo: string;
  /** Desfecho por canal. Vazio em "Ver link", que não dispara nada. */
  linhas: LinhaDisparo[];
  /** Link em claro. Ausente quando não há token recuperável (convite antigo). */
  link?: string;
  /**
   * O endereço mudou nesta ação? `true` = o anterior morreu (Novo link, ou reenvio de
   * convite antigo); `false` = é o mesmo que o fornecedor já tem (Ver link, e o reenvio
   * normal desde 12/08/2026). O texto do aviso é o oposto em cada caso — errar aqui faz o
   * comprador reenviar por medo, ou confiar num link morto.
   */
  linkMudou?: boolean;
  /** Quando não há link a exibir: o porquê, em texto de usuário. */
  mensagem?: string;
  /** Validade do link, já formatada. */
  expiraEm?: string;
  onFechar: () => void;
}

/**
 * Texto humano de um resultado de canal — a resposta ao "reenviado o quê, para onde".
 *
 * ⚠️ O que distingue os dois WhatsApp é `manual`, NUNCA o canal (at-07). Até 19/08/2026 este
 * ramo assumia que WhatsApp é sempre manual, porque era verdade — com a integração OctaDesk
 * ligada, o servidor envia de fato, e continuar dizendo "use o botão" mandaria o comprador
 * enviar de novo uma mensagem que já saiu.
 */
function descreverResultado(r: ResultadoDisparo): { texto: string; tom: 'ok' | 'falha' | 'manual' } {
  const nome = r.canal === 'WHATSAPP' ? 'WhatsApp' : 'E-mail';

  // Preparo do link: nada foi enviado, e o texto não pode sugerir que foi.
  if (r.canal === 'WHATSAPP' && r.manual) {
    return {
      texto: r.sucesso
        ? 'WhatsApp: envio manual — use o botão WhatsApp na linha do fornecedor.'
        : `WhatsApp: não foi possível preparar o envio — ${r.erro ?? 'falha desconhecida'}.`,
      tom: r.sucesso ? 'manual' : 'falha',
    };
  }

  if (r.sucesso) {
    return { texto: `${nome} enviado para ${r.destinatario}.`, tom: 'ok' };
  }
  return {
    texto: `${nome} NÃO enviado${r.destinatario ? ` para ${r.destinatario}` : ''} — ${r.erro ?? 'falha desconhecida'}.`,
    tom: 'falha',
  };
}

const ResultadoDisparoModal: React.FC<ResultadoDisparoModalProps> = ({
  titulo,
  linhas,
  link,
  linkMudou = true,
  mensagem,
  expiraEm,
  onFechar,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [copiado, setCopiado] = useState(false);

  async function copiarLink() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
    } catch {
      // Clipboard bloqueado (permissão/contexto): seleciona o campo para o Ctrl+C manual.
      inputRef.current?.select();
      notify.warning('Não foi possível copiar automaticamente. O link está selecionado — use Ctrl+C.');
    }
  }

  return (
    <div
      className="add-car-modal-overlay"
      onMouseDown={(e) => {
        if (modalRef.current && !modalRef.current.contains(e.target as Node)) onFechar();
      }}
    >
      <div className="fornecedor-modal-content resultado-disparo-modal" ref={modalRef}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>{titulo}</h2>
          </div>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          {/* "Ver link" não dispara nada: sem linhas, a seção de canais não existe. */}
          <div className="fornecedor-modal-secao" hidden={linhas.length === 0}>
            {linhas.map((linha) => (
              <div key={linha.fornecedorNome} className="resultado-disparo-fornecedor">
                <span className="fornecedor-contato-nome">{linha.fornecedorNome}</span>
                <ul className="resultado-disparo-lista">
                  {linha.resultados.length === 0 ? (
                    <li className="resultado-disparo-item resultado-disparo-item--falha">
                      Nenhum canal disparado — o fornecedor não tem contato cadastrado.
                    </li>
                  ) : (
                    linha.resultados.map((r, i) => {
                      const { texto, tom } = descreverResultado(r);
                      return (
                        <li key={i} className={`resultado-disparo-item resultado-disparo-item--${tom}`}>
                          {texto}
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
            ))}
          </div>

          {!link && mensagem && (
            <div className="fornecedor-modal-secao">
              <h3>Link do fornecedor</h3>
              <p className="fornecedor-modal-hint">{mensagem}</p>
            </div>
          )}

          {link && (
            <div className="fornecedor-modal-secao">
              <h3>Link do fornecedor</h3>
              <p className="fornecedor-modal-hint">
                {linkMudou ? (
                  <>
                    Este é o link que vale agora — copie se for entregá-lo por outro meio.{' '}
                    <strong>O link anterior deixou de valer.</strong>
                  </>
                ) : (
                  <>
                    <strong>É o mesmo link que o fornecedor já recebeu</strong> e ele continua valendo —
                    ver ou reenviar não troca o endereço. Para invalidar o atual e gerar outro, use
                    "Novo link".
                  </>
                )}
                {expiraEm ? ` Vale até ${expiraEm}.` : ''}
              </p>
              <div className="resultado-disparo-link-row">
                <input
                  ref={inputRef}
                  className="fornecedor-modal-input"
                  readOnly
                  value={link}
                  onFocus={(e) => e.target.select()}
                  aria-label="Link do fornecedor"
                />
                <button
                  type="button"
                  className="fornecedor-modal-btn fornecedor-modal-btn--primario"
                  onClick={copiarLink}
                >
                  {copiado ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </div>
          )}

          <div className="fornecedor-modal-acoes">
            <button type="button" className="fornecedor-modal-btn" onClick={onFechar}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultadoDisparoModal;
